import { faArrowRightLong } from "@jewlr/pro-regular-svg-icons/faArrowRightLong"
import { faCircle } from "@jewlr/pro-solid-svg-icons/faCircle"
import { faTruckFast } from "@jewlr/sharp-light-svg-icons/faTruckFast"
import { faDiamond } from "@jewlr/sharp-solid-svg-icons/faDiamond"
import { faSquare } from "@jewlr/sharp-solid-svg-icons/faSquare"
import PropTypes from "prop-types"
import { useEffect, useMemo, useState, useCallback, memo } from "react"
import { useSelector } from "react-redux"
import { Link, useLocation } from "react-router-dom"

import { openModal } from "components/modal"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Media from "components/ui/media"
import { jewlr_t, isTestMode, IS_GE_CHECKOUT } from "helpers/application"
import usePopupTrigger from "helpers/subscribe"
import { cn } from "styles/utils"

import Clock from "./clock"
import Markdown from "./markdown"

const TextItem = memo(
  ({ currentIndex, i, id, isSecondMicroBanner, text, triggerSignUp }) => {
    const triggerPopup = useMemo(() => {
      let trigger = null

      switch (text.type) {
        case "sign-up":
        case "sign-up-countdown":
          trigger = () => {
            if (!isTestMode) {
              dataLayer.push({ event: "trigger-micro-banner-sign-up-unit" })
            }
            triggerSignUp()
          }
          break
        case "modal":
        case "modal-countdown":
          trigger = () => openModal(text.url)
          break
      }

      return trigger
    }, [text.type, text.url, triggerSignUp])

    const { Comp, compProps } = useMemo(() => {
      let Comp = "div"
      let compProps = null

      const hasCodeBlock = text.rich_text && text.rich_text.includes("`")

      if (!hasCodeBlock) {
        if (triggerPopup) {
          Comp = Button
          compProps = { onClick: triggerPopup, text: true }
        } else if (text.url) {
          Comp = Link
          compProps = { to: text.url }
        }
      }

      return { Comp, compProps }
    }, [text.url, text.rich_text, triggerPopup])

    const isActive = currentIndex === i

    return (
      <div
        className={cn(
          "col-start-1 row-start-1 flex items-center justify-center transition-opacity duration-500",
          !isSecondMicroBanner && "large:justify-end",
          isActive ? "z-0 opacity-100 delay-300" : "-z-10 opacity-0 delay-0"
        )}
      >
        {text.type?.includes("countdown") ? (
          <Clock
            Comp={Comp}
            compProps={compProps}
            isSecondMicroBanner={isSecondMicroBanner}
            text={text}
            triggerPopup={triggerPopup}
          />
        ) : text.type === "delivery" ? (
          <Comp
            className="flex items-center justify-center"
            data-lc="countdown-banner"
            {...compProps}
          >
            <FontAwesomeIcon className="text-20 mr-8" icon={faTruckFast} />
            {text.rich_text ? (
              <Markdown
                classNames={text.style?.classNames}
                defaultClassName="large:text-15 text-14 text-left"
                triggerPopup={triggerPopup}
                type={text.type}
              >
                {text.rich_text}
              </Markdown>
            ) : (
              <span
                className={cn(
                  "large:text-15 text-14 text-left",
                  text.style?.className
                )}
                dangerouslySetInnerHTML={{ __html: text.text }}
              />
            )}
          </Comp>
        ) : (
          <Comp
            className="flex items-center"
            data-lc="countdown-banner"
            {...compProps}
          >
            {id === "desktop" && !["sign-up", "modal"].includes(text.type) && (
              <FontAwesomeIcon className="text-12 mr-6" icon={faDiamond} />
            )}
            {text.rich_text ? (
              <Markdown
                classNames={text.style?.classNames}
                defaultClassName="large:text-15 text-14 text-left"
                triggerPopup={triggerPopup}
                type={text.type}
              >
                {text.rich_text}
              </Markdown>
            ) : (
              <span
                className={cn("large:text-15 text-14", text.style?.className)}
                dangerouslySetInnerHTML={{ __html: text.text }}
              />
            )}
            {id === "desktop" && !["sign-up", "modal"].includes(text.type) && (
              <FontAwesomeIcon
                className="text-12 ml-6"
                icon={faArrowRightLong}
              />
            )}
          </Comp>
        )}
      </div>
    )
  }
)
TextItem.displayName = "TextItem"

TextItem.propTypes = {
  currentIndex: PropTypes.number.isRequired,
  i: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  isSecondMicroBanner: PropTypes.bool,
  text: PropTypes.object.isRequired,
  triggerSignUp: PropTypes.func.isRequired,
}

const TextRoller = memo(({ currentIndex, id, isSecondMicroBanner, texts }) => {
  const triggerSignUp = usePopupTrigger()

  return (
    <div className="grid h-full w-full overflow-hidden">
      {texts.map((text, i) => (
        <TextItem
          currentIndex={currentIndex}
          i={i}
          id={id}
          isSecondMicroBanner={isSecondMicroBanner}
          key={`microbanner-${i}-${id}-${text.id || text.text}`}
          text={text}
          triggerSignUp={triggerSignUp}
        />
      ))}
    </div>
  )
})
TextRoller.displayName = "TextRoller"

TextRoller.propTypes = {
  currentIndex: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  isSecondMicroBanner: PropTypes.bool,
  texts: PropTypes.array.isRequired,
}

const CountdownBanner = ({
  microbanner,
  isSecondMicroBanner,
  showOnDesktop = true,
  showOnMobile = true,
}) => {
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0)
  const [currentDesktopIndex, setCurrentDesktopIndex] = useState(0)
  const isGeCheckout = useMemo(() => IS_GE_CHECKOUT(), [])

  const showPropsOnDesktop = !isSecondMicroBanner

  const location = useLocation()
  const isProductPage = location.pathname.includes("/products/")
  const isHomepage = location.pathname === "/"
  const productTags = useSelector((state) => state.wizard.tags)
  const productTagsSet = useMemo(
    () => new Set(productTags || []),
    [productTags]
  )

  const textFilter = useCallback(
    ({ type, meta }, hasPdpInList, activeExp) => {
      // 1. Variant check: If variant is defined, it must match activeExp
      if (
        activeExp != null &&
        typeof meta?.variant !== "undefined" &&
        meta.variant !== activeExp
      ) {
        return false
      }

      const isPdpType = type === "pdp" || type === "pdp-countdown"

      // 2. Conditional Page Filtering
      if (hasPdpInList) {
        if (isPdpType !== isProductPage) {
          return false
        }
      }

      // 3. Product tag check: only relevant for PDP types
      if (isPdpType) {
        const tags = meta?.product_tags || []
        if (tags.length > 0 && !tags.some((tag) => productTagsSet.has(tag))) {
          return false
        }
      }

      return true
    },
    [isProductPage, productTagsSet]
  )

  const desktopTexts = useMemo(() => {
    const texts = microbanner.desktop_texts || []
    const hasPdp = texts.some(
      (t) => t.type === "pdp" || t.type === "pdp-countdown"
    )
    return texts.filter((text) => textFilter(text, hasPdp))
  }, [microbanner.desktop_texts, textFilter])

  const mobileTexts = useMemo(() => {
    const texts = microbanner.mobile_texts || []
    const hasPdp = texts.some(
      (t) => t.type === "pdp" || t.type === "pdp-countdown"
    )
    return texts.filter((text) => textFilter(text, hasPdp))
  }, [microbanner.mobile_texts, textFilter])

  const { className, ...style } = microbanner.style || {}

  const handleClickForSimplifyAboveFoldExp = useCallback(() => {
    if (!isTestMode && isProductPage && isSecondMicroBanner) {
      dataLayer.push({
        event: "clicked-countdown-promotion-pdp",
      })
    }
  }, [isProductPage, isSecondMicroBanner])

  const handleAnnouncementBarClick = useCallback(() => {
    if (!isTestMode && isHomepage && !isSecondMicroBanner) {
      dataLayer.push({
        event: "clicked-announcement-bar",
      })
    }
  }, [isHomepage, isSecondMicroBanner])

  const handleSalesBannerClick = useCallback(() => {
    if (!isTestMode && isHomepage && isSecondMicroBanner) {
      dataLayer.push({
        event: "clicked-sales-banner",
      })
    }
  }, [isHomepage, isSecondMicroBanner])

  useEffect(() => {
    if (mobileTexts.length > 0) {
      const mobileInterval = setInterval(() => {
        setCurrentMobileIndex(
          (currentIndex) => (currentIndex + 1) % mobileTexts.length
        )
      }, 5000)
      return () => clearInterval(mobileInterval)
    }
  }, [mobileTexts])

  useEffect(() => {
    if (desktopTexts.length > 0) {
      const desktopInterval = setInterval(() => {
        setCurrentDesktopIndex(
          (currentIndex) => (currentIndex + 1) % desktopTexts.length
        )
      }, 5000)
      return () => clearInterval(desktopInterval)
    }
  }, [desktopTexts])

  return (
    <>
      {mobileTexts?.length > 0 && showOnMobile && (
        <Media
          className={cn(
            "bg-black-print w-full px-16 text-white",
            isSecondMicroBanner
              ? "bg-primary h-(--second-microbanner-height)"
              : "h-(--countdown-banner-height)",
            className
          )}
          lessThan={isSecondMicroBanner ? "desktop" : "large"}
          {...style}
        >
          <div
            className={cn(
              "large:px-16 relative flex h-full w-full items-center justify-center text-center",
              !isSecondMicroBanner && "large:justify-between"
            )}
            onClick={() => {
              handleClickForSimplifyAboveFoldExp()
              if (isSecondMicroBanner) {
                handleSalesBannerClick()
              } else {
                handleAnnouncementBarClick()
              }
            }}
          >
            <TextRoller
              currentIndex={currentMobileIndex}
              id="mobile"
              isSecondMicroBanner={isSecondMicroBanner}
              texts={mobileTexts}
            />
          </div>
        </Media>
      )}
      {(!isSecondMicroBanner || desktopTexts?.length > 0) && showOnDesktop && (
        <Media
          className={cn(
            "bg-black-print w-full text-white",
            isSecondMicroBanner
              ? "bg-primary h-(--second-microbanner-height)"
              : "h-(--countdown-banner-height)",
            className
          )}
          greaterThanOrEqual={isSecondMicroBanner ? "desktop" : "large"}
          {...style}
        >
          <div
            className={cn(
              "large:px-16 relative flex h-full w-full items-center justify-center text-center",
              showPropsOnDesktop && "large:justify-between"
            )}
            onClick={() => {
              if (isSecondMicroBanner) {
                handleSalesBannerClick()
              } else {
                handleAnnouncementBarClick()
              }
            }}
          >
            {!showPropsOnDesktop ? (
              <TextRoller
                currentIndex={currentDesktopIndex}
                id="desktop"
                isSecondMicroBanner
                texts={desktopTexts}
              />
            ) : (
              <>
                <ul
                  className={cn(
                    "text-15 inline-flex list-none items-center font-normal",
                    "[&_li]:inline-flex [&_li]:items-center [&_li]:px-6"
                  )}
                >
                  <li>
                    <FontAwesomeIcon
                      className="text-12 mr-6"
                      icon={faDiamond}
                    />
                    <Link to="/info/shipping_information">
                      FREE {jewlr_t("terms.Shipping")}
                    </Link>
                  </li>
                  <li>
                    <FontAwesomeIcon className="text-10 mr-6" icon={faCircle} />
                    <Link to="/info/returns">
                      {isGeCheckout ? "30" : "99"}-Day Returns
                    </Link>
                  </li>
                  <li>
                    <FontAwesomeIcon className="text-12 mr-6" icon={faSquare} />
                    <Link to="/info/warranty-information">
                      One-Year Warranty
                    </Link>
                  </li>
                </ul>
                {desktopTexts.length > 0 && (
                  <div className="relative">
                    <TextRoller
                      currentIndex={currentDesktopIndex}
                      id="desktop"
                      isSecondMicroBanner={false}
                      texts={desktopTexts}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Media>
      )}
    </>
  )
}

CountdownBanner.propTypes = {
  isSecondMicroBanner: PropTypes.bool,
  microbanner: PropTypes.object,
  showOnDesktop: PropTypes.bool,
  showOnMobile: PropTypes.bool,
}

export default CountdownBanner
